import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Loading } from "@/components/common";
import { BrandLockup } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PortalLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <Loading label="Verificando sessão…" />;
  if (user) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const result = await login(identifier.trim(), password);
    setSubmitting(false);
    if (result.ok) navigate("/portal");
    else setError(result.error);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="portal-login-page" className="relative grid min-h-screen bg-bone lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-sage-700 p-12 text-bone lg:flex">
        <img
          data-testid="portal-login-photo"
          src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/media/estrutura/ADV6816-screen.jpg`}
          alt="Área arborizada da Clínica Erimus, em Atibaia"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sage-700/70 via-sage-700/65 to-sage-700/90" />
        <div className="relative">
          <BrandLockup size="md" tone="dark" product="Portal Erimus" testId="portal-login-brand" />
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-semibold leading-[1.15] tracking-tight">Operação clínica interna</h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-bone/85">
            Acesso restrito à equipe autorizada. Toda ação é registrada em auditoria append-only e a autorização de
            cada vínculo é revalidada a cada chamada.
          </p>
        </div>
        <p className="relative text-[13px] text-bone/70">Acesso restrito à equipe autorizada da Erimus</p>
      </div>

      <Link
        to="/brand"
        data-testid="open-brand-board"
        className="absolute right-6 top-6 text-xs font-semibold uppercase tracking-[0.18em] text-sage-600 transition-colors hover:text-sage-700"
      >
        Identidade visual
      </Link>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="er-rise w-full max-w-md">
          <p className="er-label text-[12px] tracking-[0.16em]">Acesso interno</p>
          <h2 className="mt-2.5 font-display text-[28px] font-semibold leading-[1.2] tracking-tight text-ink-900">
            Entrar no portal
          </h2>
          <p className="mt-2.5 text-[15px] leading-relaxed text-ink-600">
            Use e-mail e senha da equipe. MFA será habilitada em etapa futura.
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="identifier" className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
                E-mail ou nome de usuário
              </Label>
              <Input
                id="identifier"
                data-testid="portal-email-input"
                type="text"
                value={identifier}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-2 h-12 rounded-xl bg-white text-[14.5px]"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
                Senha
              </Label>
              <Input
                id="password"
                data-testid="portal-password-input"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 rounded-xl bg-white text-[14.5px]"
                required
              />
            </div>
          </div>

          {error ? (
            <p data-testid="portal-login-error" role="alert" className="mt-5 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            data-testid="portal-login-submit"
            disabled={submitting}
            className="mt-8 h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700"
          >
            {submitting ? "Entrando…" : "Entrar"}
          </Button>

          <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-ink-400">
            <span className="h-px flex-1 bg-border" /> ou <span className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            data-testid="portal-google-login"
            onClick={googleLogin}
            className="h-12 w-full rounded-full border-border bg-white text-ink-700 hover:bg-sand"
          >
            <KeyRound size={16} className="mr-2" /> Entrar com Google (equipe interna)
          </Button>
          <p className="mt-3 text-[13px] leading-relaxed text-ink-500">
            O login Google funciona apenas para e-mails já cadastrados como equipe interna.
          </p>
        </form>
      </div>
    </div>
  );
}
